import { useEffect, useMemo, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { TopBranchBanner } from "./TopBranchBanner";

interface BranchData {
  branchName: string;
  reqs: number;
  subs: number;
  ecs: number;
  eci: number;
  po: number;
}

interface FieldMeta {
  key: keyof Omit<BranchData, "branchName">;
  full: string;
  icon: string;
  bg: string;
  text: string;
  iconBg?: string;
}

type FilterOption = "previousDay" | "previousWeek" | "previousMonth";

const FILTER_TABS: { label: string; short: string; value: FilterOption; icon: string }[] = [
  { label: "Previous Day", short: "Day", value: "previousDay", icon: "pi-sun" },
  { label: "Previous Week", short: "Week", value: "previousWeek", icon: "pi-calendar" },
  { label: "Previous Month", short: "Month", value: "previousMonth", icon: "pi-calendar-plus" },
];

const FIELDS: FieldMeta[] = [
  { key: "reqs", full: "Requirements", icon: "pi pi-briefcase", bg: "#EBF1FD", iconBg: "#EBF1FD", text: "#17509D" },
  { key: "subs", full: "Submissions", icon: "pi pi-file", bg: "#E1F5EE", text: "#1D7358" },
  { key: "ecs", full: "ECS", icon: "pi pi-check-square", bg: "#FBEAF0", text: "#90365C" },
  { key: "eci", full: "ECI", icon: "pi pi-user-plus", bg: "#FAEEDA", text: "#854110ed" },
  { key: "po", full: "Purchase  ordered", icon: "pi pi-flag", bg: "#EEEDFE", text: "#3C3489" },
];

const fmtInt = (n: number): string => Math.round(n).toLocaleString("en-IN");

const fmtDate = (d: Date): string => d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

const daysAgo = (offsetDays: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d;
};

const getPeriodLabel = (filter: FilterOption): string => {
  switch (filter) {
    case "previousDay":
      return "Previous Day's";
    case "previousWeek":
      return "Previous Week's";
    case "previousMonth":
      return "Previous Month's";
  }
};

export const BranchWiseReport = () => {
  const [branches, setBranches] = useState<BranchData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterOption>("previousDay");

  const { fromDate, toDate } = useMemo(() => {
    switch (filter) {
      case "previousDay": {
        const d = daysAgo(1);
        return { fromDate: d, toDate: d };
      }
      case "previousWeek": {
        return { fromDate: daysAgo(7), toDate: daysAgo(1) };
      }
      case "previousMonth": {
        const today = new Date();
        const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const to = new Date(today.getFullYear(), today.getMonth(), 0); // last day of previous month
        return { fromDate: from, toDate: to };
      }
    }
  }, [filter]);

  const periodLabel = getPeriodLabel(filter);
  const activeIndex = FILTER_TABS.findIndex((t) => t.value === filter);
  const daysCount = Math.round((toDate.getTime() - fromDate.getTime()) / 86400000) + 1;

  const dateRangeLabel = fromDate.toDateString() === toDate.toDateString() ? fmtDate(toDate) : `${fmtDate(fromDate)} - ${fmtDate(toDate)}`;

  const handleTabClick = (value: FilterOption) => {
    setFilter(value);
  };

  const getBranchWiseReport = async (from: Date, to: Date) => {
    setLoading(true);
    const body = { fromDate: from, toDate: to };
    const response = await Fetch("CreativityDashboard/getBranchDataReport", "POST", body, null, null);
    if (response) {
      const data: BranchData[] = await response.json();
      setBranches(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getBranchWiseReport(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, fromDate.getTime(), toDate.getTime()]);

  const ranked = [...branches].sort((a, b) => b.po - a.po);
  const maxPO = ranked[0]?.po || 1;
  const totals = FIELDS.reduce((acc, f) => {
    acc[f.key] = branches.reduce((s, d) => s + d[f.key], 0);
    return acc;
  }, {} as Record<FieldMeta["key"], number>);

  const statFields = FIELDS.filter((f) => f.key !== "po");

  return (
    // <div className="flex flex-column" style={{ maxWidth: 980, margin: "0 auto" }}>
    <div className="flex flex-column" style={{ maxWidth: 1400, width: "100%", margin: "0 auto", padding: "0 16px" }}>
      {/* Header */}
      <style>{`@keyframes pulseDot { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }`}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap", gap: 20, marginBottom: "1.25rem", padding: "13px 20px", background: "#fff", border: "1px solid #EDECF3", borderRadius: 12, overflowX: "auto" }}>
        {/* Left: title + subtitle (status + range folded in) */}
        <div style={{ minWidth: 0, flexShrink: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h5 style={{ margin: 0, fontSize: 17, fontWeight: 650, color: "#111827", letterSpacing: -0.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{periodLabel} Production Summary</h5>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, letterSpacing: 0.3, color: "#6B7280", flexShrink: 0 }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22C55E", animation: "pulseDot 2s ease-in-out infinite" }} />
              Live
            </span>
          </div>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#9CA3AF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {dateRangeLabel} · {daysCount} day{daysCount === 1 ? "" : "s"}
          </p>
        </div>

        {/* Right: segmented filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: "auto" }}>
          <div style={{ position: "relative", display: "flex", background: "#F7F7F9", border: "1px solid #EDECF3", borderRadius: 9, padding: 3, gap: 2 }}>
            <div
              style={{
                position: "absolute",
                top: 3,
                left: 3,
                width: `calc(${100 / FILTER_TABS.length}% - 2px)`,
                height: "calc(100% - 6px)",
                background: "#fff",
                border: "1px solid #E4E1F7",
                borderRadius: 7,
                boxShadow: "0 1px 2px rgba(60,52,137,0.06)",
                transform: `translateX(${activeIndex * 100}%)`,
                transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
            {FILTER_TABS.map((tab) => {
              const isActive = filter === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => handleTabClick(tab.value)}
                  style={{
                    position: "relative",
                    zIndex: 1,
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#3C3489" : "#6B7280",
                    background: "transparent",
                    border: "none",
                    borderRadius: 7,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "color 0.15s",
                  }}
                >
                  <i className={`pi ${tab.icon}`} style={{ fontSize: 10.5 }} />
                  {tab.short}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="w-full text-center p-4">Loading branch report…</div>
      ) : (
        <>
          {/* Top Performer */}
          {ranked.length > 0 && ranked[0].po > 0 && <TopBranchBanner data={ranked[0]} periodLabel={periodLabel} />}

          {/* Leaderboard */}
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0.95rem 1.25rem", borderBottom: "1px solid #f0f0f1" }}>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: "#EEEDFE", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <i className="pi pi-star-fill" style={{ fontSize: 13, color: "#7F77DD" }} />
              </span>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14.5 }}>Top Performers by Purchase ordered</p>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#9D9D9D" }}>Yield % = ( ECI / Requirements ) × 100</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "40px minmax(160px,1.6fr) repeat(5, 1fr)", alignItems: "center", padding: "0.6rem 1.25rem", background: "#fafafa", borderBottom: "1px solid #f0f0f1" }}>
              <span />
              <span />
              {FIELDS.map((f) => (
                <span key={f.key} style={{ fontSize: 11, color: "black", fontWeight: 500 }}>
                  {f.full}
                </span>
              ))}
            </div>

            {ranked.map((d, i) => {
              const rank = i + 1;
              const isTop = rank === 1;
              const pct = (d.po / maxPO) * 100;
              const yieldPct = d.reqs ? ((d.eci / d.reqs) * 100).toFixed(1) : "0.0";

              return (
                <div key={d.branchName} style={{ display: "grid", gridTemplateColumns: "40px minmax(160px,1.6fr) repeat(5, 1fr)", alignItems: "center", padding: "0.85rem 1.25rem", borderBottom: i === ranked.length - 1 ? "none" : "1px solid #f0f0f1" }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: isTop ? "#EEEDFE" : "#f4f4f5", color: isTop ? "#3C3489" : "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 600 }}>{rank}</div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <p style={{ fontSize: 14.5, fontWeight: 600, margin: 0 }}>{d.branchName}</p>
                      {isTop && <span style={{ fontSize: 10.5, background: "#EBEAF8", color: "#4D3FA0", padding: "2px 7px", borderRadius: 5, fontWeight: 500 }}>Top Performer</span>}
                    </div>
                    <span style={{ fontSize: 11.5, color: "#32886F" }}>Yield {yieldPct}%</span>
                  </div>

                  {statFields.map((f) => (
                    <span key={f.key} style={{ fontSize: 13.5, color: "#111827", display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <i className={f.icon} style={{ fontSize: 12, color: f.text }} />
                      {fmtInt(d[f.key])}
                    </span>
                  ))}

                  <div>
                    <p style={{ fontSize: 15.5, fontWeight: 700, margin: "0 0 6px", color: "#3C3489" }}>{fmtInt(d.po)}</p>
                    <div style={{ height: 5, background: "#f4f4f5", borderRadius: 6, overflow: "hidden", width: 90 }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: "#7F77DD" }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* KPI row (Total Count Row) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px", marginBottom: "16px", marginTop: "8px" }}>
            {FIELDS.map((f) => (
              <div key={f.key} style={{ background: f.bg, borderRadius: "8px", padding: "8px 10px", border: "1px solid #E5E7EB" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                  <i className={f.icon} style={{ color: f.text, fontSize: "12px" }} />
                  <span style={{ fontSize: "11px", fontWeight: 600, color: f.text }}>{f.full}</span>
                </div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", marginTop: "4px" }}>
                  Total {f.full}: {fmtInt(totals[f.key])}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
